import { enableProdMode, NgModuleRef } from '@angular/core';
import { logPerformance } from '@office-start/telemetry-service';
import { filter, take } from 'rxjs/operators';

import { isAppShell } from './app-shell/is-app-shell.utility';
import { AppModule } from './app.module';
import { OfficeHomePageErrorHandler } from './error-handler/error-handler.service';
import { ErrorId } from './error-handler/error-id';
import { ServiceWorkerInitializer } from './service-worker/service-worker-initializer.service';
import { ServiceWorkerStateService } from './service-worker/service-worker-state.service';
import { onAngularBootstrapError } from './shared/bootstrap/bootstrap-handler';
import { ActivityEventFactoryService } from './shared/instrumentation/activity-event-factory.service';
import { ActivitySessionService } from './shared/instrumentation/activity-session.service';
import { AriaEventFactoryService } from './shared/instrumentation/aria.service';
import { ClickLogging } from './shared/instrumentation/click-logging.utility';
import { DispatcherInstrumentationService } from './shared/instrumentation/dispatcher-instrumentation.service';
import { InstrumentationService, KnownInstrumentationEvents } from './shared/instrumentation/instrumentation.service';
import { UserProfileEventFactoryService, UserProfileService } from './shared/instrumentation/user-profile.service';
import { ObservableScope } from './shared/models/observable-scope.enum';
import { OfficeStartBootstrapperService } from './shared/office-start/office-start-bootstrapper.service';
import { AccessTokenService } from './shared/services/access-token.service';
import { AuthStateService } from './shared/services/auth-state.service';
import { DocumentService } from './shared/services/document.service';
import { EventService } from './shared/services/event.service';
import { OfficeRouteService } from './shared/services/office-route.service';
import { KnownPerformanceMarks, Performance } from './shared/services/performance.service';
import { AngularEvents } from './shared/shared-context/angular-events';
import { CapabilitiesService } from './shared/shared-context/capabilities.service';
import { ClientStartupConfig, getStartupConfiguration } from './shared/shared-context/client-startup-config.service';
import { DOMInteractiveMarker } from './shared/utilities/dom-interactive-marker.utility';

import '../less/HomePage.css';

const enum AppState {
    NotInitialized,
    Initializing,
    Initialized
}

let bootstrapStartTime: number;
let bootstrapEndTime: number;
let appState = AppState.NotInitialized;
let loggerInitialized = false;
let startupConfig: ClientStartupConfig;

// Runs just before Angular Application Bootstrap starts.
export function preBootstrap() {
    startupConfig = getStartupConfiguration();

    // Angular 2 Production Mode is disabled by default.
    if (!startupConfig.angularDebugInfoEnabled) {
        enableProdMode();
    }

    bootstrapStartTime = Performance.now();
}

// Marker that runs just when Angular bootstrap completes.
export function markAppBootstrapCompleted() {
    return () => bootstrapEndTime = Performance.now();
}

// Runs after Angular Application Bootstrap
export function postBootstrap(platformRef: NgModuleRef<AppModule>) {
    // IMPORTANT: Take special care when modifying postBootstrap implementation. This can run before entire page response is returned
    // so there is no guarantee that all config JSON which is needed for initializing most of app-specific code is already present.
    // Only config that is guaranteed to precede execution of this code is available via ClientStartupConfigService.
    // Ensure that any code relying on other configs moved inside initializeApp() function below.
    const rootInjector = platformRef.injector;

    // IMPORTANT: Instantiate EventService as early as possible. The instance is exposed globally to be used by various non-Angular
    // javascript to notify app about events happening outside of Angular world. For instance, this service is used by Shell and Support Central scripts.
    let eventService = rootInjector.get(EventService);

    const initializeApp = (checkForShellInfoLoadedIndicatorPresence: boolean) => {
        try {
            if (appState !== AppState.NotInitialized) {
                return false;
            }

            appState = AppState.Initializing;

            if (checkForShellInfoLoadedIndicatorPresence) {
                // Validate if shell data is already available by checking for presence of element that is placed after all config elements.
                // This check is needed because it is possible that page is parsed by browser before Angular is fully initialized.
                const documentService = rootInjector.get(DocumentService);
                const shellInfoLoadedIndicator = documentService.nativeDocument.getElementById('ShellInfoLoaded');
                if (!shellInfoLoadedIndicator) {
                    appState = AppState.NotInitialized;
                    return false;
                }
            }

            // At this point it is safe to start accessing services because all configs that services rely on has been
            // processed by DOM parser. First order of business is to initialize instrumentation because it is used everywhere.
            // We cannot encapsulate this initialization inside InstrumentationService due cyclic dependencies between all
            // services involved in initialization.
            const serviceWorkerInitializer = rootInjector.get(ServiceWorkerInitializer);
            serviceWorkerInitializer.initialize();
            const routeService = rootInjector.get(OfficeRouteService);
            const serviceWorkerStateService = rootInjector.get(ServiceWorkerStateService);
            routeService.start();
            const instrumentationService = rootInjector.get(InstrumentationService);
            ClickLogging.setup(instrumentationService);

            const bootStart = Math.ceil(bootstrapStartTime);
            instrumentationService.logPerformanceEvent(KnownPerformanceMarks.AppBeginBootstrap, bootStart);
            instrumentationService.logPerformanceEvent(KnownPerformanceMarks.AppCompleteBootstrap, Math.ceil(bootstrapEndTime) - bootStart);
            logPerformance({eventName: KnownPerformanceMarks.AppBeginBootstrap, latency: bootStart });
            logPerformance({eventName: KnownPerformanceMarks.AppCompleteBootstrap, latency: Math.ceil(bootstrapEndTime) - bootStart });

            const activitySessionService = rootInjector.get(ActivitySessionService);
            activitySessionService.initialize(instrumentationService);

            const capabilitiesService = rootInjector.get(CapabilitiesService);
            const otherFlights = capabilitiesService && capabilitiesService.capabilities.otherFlights || '';

            const appStartupConfig = getStartupConfiguration();
            const initialFlights = appStartupConfig && appStartupConfig.sharedClientStartupContext.flights ? appStartupConfig.sharedClientStartupContext.flights : '';

            // Important: flights needs to be updated before AriaService is initialized, which happens on the call to
            appStartupConfig.sharedClientStartupContext.flights = `${initialFlights}${initialFlights ? ',' : ''}${otherFlights}`;

            if (instrumentationService instanceof DispatcherInstrumentationService) {
                instrumentationService.registerOfficeRouteService(routeService);
                instrumentationService.registerServiceWorkerStateService(serviceWorkerStateService, true);
                const userProfileEventFactoryService = rootInjector.get(UserProfileEventFactoryService);
                const activityEventFactoryService = rootInjector.get(ActivityEventFactoryService);
                const ariaEventFactoryService = rootInjector.get(AriaEventFactoryService);
                instrumentationService.registerEventFactories([userProfileEventFactoryService, activityEventFactoryService, ariaEventFactoryService]);
            }

            if (appStartupConfig.sharedClientStartupContext.oTelEnabled) {
                const officeStartBootstrapperService = rootInjector.get(OfficeStartBootstrapperService);
                officeStartBootstrapperService.bootstrapOfficeStart();
            }

            // Mark logger as fully initialized; any exception thrown before logger is fully initialized will be treated as bootstrap error
            // and logged via minimal logger
            loggerInitialized = true;

            // Initialize AuthStateService; cannot be encapsulated due to cyclic dependencies.
            const accessTokenService = rootInjector.get(AccessTokenService);
            const authStateService = rootInjector.get(AuthStateService);
            authStateService.initialize(accessTokenService);

            // Broadcast event indicating that app initialization is completed. This is when app UI can be safely populated
            // based on config data.
            eventService = rootInjector.get(EventService);
            eventService.broadcastEvent({
                name: AngularEvents.appInitialized,
                args: []
            });

            const handlePrefetchers = async (northStarEnabled, edgeworthReactEnabled) => {
                if (edgeworthReactEnabled) {
                    const officeStartBootstrapperService = rootInjector.get(OfficeStartBootstrapperService);
                    officeStartBootstrapperService.bootstrapOfficeStart();

                    const dataPrefetchModulePromise = import(/*webpackChunkName: "data-prefetch-service" */ '@office-start/data-prefetch-service');

                    if (northStarEnabled) {
                        // prefetchNorthStar must execute before prefetchEdgeworthData
                        const northStarPrefetchModulePromise = import(/*webpackChunkName: "northstar-prefetch-service" */ '@office-start/northstar-prefetch-service');
                        northStarPrefetchModulePromise.then(northStarPrefetchMod => {
                            northStarPrefetchMod.prefetchNorthStar();
                            dataPrefetchModulePromise.then(dataPrefetchMod => {
                                dataPrefetchMod.prefetchEdgeworthData();
                            });
                        });
                    } else {
                        dataPrefetchModulePromise.then(dataPrefetchMod => {
                            dataPrefetchMod.prefetchEdgeworthData();
                        });
                    }
                }
            };

            handlePrefetchers(capabilitiesService.capabilities.northStarEnabled, capabilitiesService.capabilities.edgeworthReactEnabled);

            if (capabilitiesService.capabilities.showSearch) {
                instrumentationService.logGenericEvent(KnownInstrumentationEvents.SearchCapability);
            }

            const userProfileService = rootInjector.get(UserProfileService);
            if (userProfileService.isFirstVisit()) {
                instrumentationService.logGenericEvent(KnownInstrumentationEvents.FirstSiteVisit);
            }

            appState = AppState.Initialized;

            return true;
        } catch (ex) {
            appState = AppState.NotInitialized;

            // If full logger service has not initialized yet then exception will be treated as bootstrap error and
            // logged appropriately by basic logger
            let handled = false;
            if (loggerInitialized) {
                const errorHandlerService = rootInjector.get(OfficeHomePageErrorHandler);
                if (errorHandlerService) {
                    errorHandlerService.handleError(ex, ErrorId.b0g6P);
                    handled = true;
                }
            }

            if (!handled) {
                onAngularBootstrapError(ex, startupConfig);
            }

            return false;
        }
    };

    if (isAppShell()) {
        initializeApp(false);
    } else if (!initializeApp(true)) {
        // Attempt initializing app immediately; this will only succeed if second chunk with all configs have been received and
        // processed by DOM parser by the time bootstrapping has been completed.

        // Listen for event broadcasted from the script which is executed right after last config has been parsed
        eventService.events.withScope(ObservableScope.PastAndFuture).pipe(
            filter(event => event.name === AngularEvents.shellInfoLoaded),
            take(1))
            .toPromise()
            .then(() => initializeApp(false));

        // If for whatever reason there was a failure to broadcast shellInfoLoaded event then initialize app upon
        // parsing of the entire response
        DOMInteractiveMarker.shellAndConfigsReady().then(() => initializeApp(false));
    }
}
